import { useMemo, useState, type JSX } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  KeyRound, ShieldX, CheckCircle2, Eye, EyeOff,
} from 'lucide-react';
import { AuthShell } from '../components/shell/AuthShell';

/**
 * Reset-password page. Reads `?token=…` from URL (sent in the reset
 * email), takes a new password, submits to POST /admin/password/reset.
 * On success, sends the user back to /signin.
 */
export function ResetPasswordPage(): JSX.Element {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get('token') ?? '';
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const problem = useMemo(() => {
    if (!token) return 'missing_token';
    if (pw1.length > 0 && pw1.length < 12) return 'too_short';
    if (pw1.length > 256) return 'too_long';
    if (pw1 && pw2 && pw1 !== pw2) return 'mismatch';
    return null;
  }, [token, pw1, pw2]);

  const strength = useMemo(() => scorePassword(pw1), [pw1]);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (problem) return;
    setBusy(true); setErr(null);
    try {
      const res = await fetch('/admin/password/reset', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token, new_password: pw1 }),
      });
      const body = await res.json().catch(() => null) as { error?: string; reason?: string } | null;
      if (!res.ok) {
        const msg = body?.error === 'invalid_token'
          ? `This link is ${body?.reason ?? 'invalid'}. Request a new reset email.`
          : body?.error === 'weak_password'
          ? `Password is too weak (${body?.reason ?? 'try something stronger'}).`
          : body?.error === 'rate_limited'
          ? 'Too many attempts — try again in a few minutes.'
          : `Error: ${body?.error ?? res.status}`;
        throw new Error(msg);
      }
      setDone(true);
      setTimeout(() => nav('/signin'), 2500);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <AuthShell
        icon={ShieldX}
        title="Invalid link"
        subtitle="This reset link is missing its token. Request a new one."
        footer={
          <Link to="/forgot-password" style={{ color: 'var(--accent-text)' }}>Start over →</Link>
        }
      >
        <></>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell
        icon={CheckCircle2}
        title="Password updated"
        subtitle="Redirecting you to sign in…"
      >
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 8, padding: '14px 16px',
            borderRadius: 12,
            background: 'rgba(52, 211, 153, 0.10)',
            border: '1px solid rgba(52, 211, 153, 0.35)',
            color: 'var(--success)',
            fontSize: 13,
          }}
        >
          <CheckCircle2 width={14} height={14} />
          You can now sign in with the new password.
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      icon={KeyRound}
      title="Choose a new password"
      subtitle="Minimum 12 characters. Use a mix of upper, lower, digits, and symbols."
      footer={
        <Link to="/signin" style={{ color: 'var(--text-muted)' }}>← Back to sign in</Link>
      }
    >
      <form onSubmit={(e) => void submit(e)} style={{ display: 'grid', gap: 12, marginTop: 4 }}>
        <div style={{ position: 'relative', display: 'flex' }}>
          <input
            type={showPw ? 'text' : 'password'}
            value={pw1}
            required autoFocus
            onChange={(e) => setPw1(e.target.value)}
            placeholder="New password"
            autoComplete="new-password"
            disabled={busy}
            style={{ flex: 1, paddingRight: 36 }}
          />
          <button
            type="button"
            onClick={() => setShowPw((v) => !v)}
            aria-label={showPw ? 'Hide password' : 'Show password'}
            style={{
              position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
              background: 'transparent', border: 'none',
              color: 'var(--text-muted)', cursor: 'pointer',
              padding: 6, borderRadius: 6,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {showPw ? <EyeOff width={14} height={14} /> : <Eye width={14} height={14} />}
          </button>
        </div>

        <StrengthMeter score={strength} length={pw1.length} />

        <input
          type={showPw ? 'text' : 'password'}
          value={pw2}
          required
          onChange={(e) => setPw2(e.target.value)}
          placeholder="Repeat new password"
          autoComplete="new-password"
          disabled={busy}
        />
        <button
          type="submit"
          className="primary"
          disabled={busy || !!problem || pw1.length === 0 || pw2.length === 0}
        >
          {busy ? 'Updating…' : 'Update password'}
        </button>
      </form>
      {problem === 'too_short' && <p className="muted" style={{ fontSize: 12 }}>Need at least 12 characters.</p>}
      {problem === 'mismatch'  && <p className="err"   style={{ fontSize: 12 }}>Passwords don't match.</p>}
      {err && <p className="err" style={{ marginTop: 14 }}>{err}</p>}
    </AuthShell>
  );
}

function scorePassword(pw: string): 0 | 1 | 2 | 3 | 4 {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 12) score += 1;
  if (pw.length >= 16) score += 1;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  return Math.min(4, score) as 0 | 1 | 2 | 3 | 4;
}

function StrengthMeter({ score, length }: { score: 0 | 1 | 2 | 3 | 4; length: number }): JSX.Element {
  const labels = ['empty', 'weak', 'fair', 'good', 'strong'];
  const tones = ['#475569', '#ef4444', '#fb923c', '#fbbf24', '#34d399'];
  return (
    <div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            style={{
              flex: 1,
              height: 4,
              borderRadius: 2,
              background: i < score ? tones[score] : 'var(--bg-overlay)',
              transition: 'background 160ms ease',
            }}
          />
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 11,
          color: 'var(--text-muted)',
        }}
      >
        <span>Strength: <span style={{ color: tones[score] }}>{labels[score]}</span></span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{length} chars</span>
      </div>
    </div>
  );
}
